import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from '../router';

import { api, ApiError } from '../api';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { Notice } from '../components/Notice';
import { PageHeader } from '../components/PageHeader';
import { activeDevices, type Device } from '../types';

interface Draft { to: string; message: string; idempotencyKey: string }

export function ComposePage() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const hasActiveDevice = activeDevices(devices).length > 0;

  useEffect(() => {
    api<{ devices: Device[] }>('/api/devices')
      .then((result) => setDevices(result.devices))
      .catch(() => setError('Could not load connected devices.'));
  }, []);

  function review(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError('');
    const data = new FormData(event.currentTarget);
    setDraft({ to: String(data.get('to')).trim(), message: String(data.get('message')), idempotencyKey: crypto.randomUUID() });
  }

  async function confirm() {
    if (!draft) return;
    setBusy(true); setError('');
    try {
      await api('/api/v1/messages', {
        method: 'POST',
        headers: { 'Idempotency-Key': draft.idempotencyKey },
        body: JSON.stringify({ to: draft.to, message: draft.message }),
      });
      navigate('/app/inbox', { state: { toast: 'Sending SMS.' } });
    } catch (caught) { setError(caught instanceof ApiError ? caught.message : 'Could not queue this message.'); }
    finally { setBusy(false); }
  }

  return (
    <div className="page narrow-page">
      <PageHeader eyebrow="Compose" title="New message" description="Your connected phone performs the carrier send. Review the exact recipient and content before sending." />
      {error && <Notice kind="error">{error}</Notice>}
      {!hasActiveDevice && <Notice kind="warning">No phone is ready. Sign in on Android and enable optional web sync first.</Notice>}
      <form className="surface-form" onSubmit={review}>
        <label>Recipient<input name="to" defaultValue={params.get('to') ?? ''} required maxLength={120} placeholder="Phone number" /></label>
        <label>Message<textarea className="message-editor" name="message" required rows={7} maxLength={10_000} placeholder="Write your message" /></label>
        <small>Messavo uses your active approved phone and automatic SIM routing.</small>
        <button className="button button-primary" disabled={!hasActiveDevice}><Icon name="fact_check" /> Review message</button>
      </form>
      {draft && <Modal title="Confirm this message" description="Check the recipient and complete content before sending." onClose={() => setDraft(null)}>
        <div className="review-card"><small>Recipient</small><strong>{draft.to}</strong><small>Automatic phone and SIM routing</small><p>{draft.message}</p></div>
        <div className="modal-actions"><button className="button button-quiet" type="button" onClick={() => setDraft(null)}>Keep editing</button><button className="button button-primary" disabled={busy} type="button" onClick={() => void confirm()}><Icon name="send" /> {busy ? 'Sending…' : 'Send now'}</button></div>
      </Modal>}
    </div>
  );
}
